import { outbreakActions } from '../outbreak-actions';
/** Park build: enclosure construction against the live catalog + owned parcels.
 * Commands per docs/COMMANDS.md: enclosure.build { parcelId, type, areaHa,
 * fencePerimeterM, waterPoints, fenceGrade }. Rejections arrive as alerts; no
 * fake acks. 4A: the player always picks the fence grade explicitly - tapping a
 * type opens the three grade options with their real totals; there is no
 * default grade. */
import { useState } from 'preact/hooks';
import { useStore } from '../store';
import { fmtMoney } from '../bridge';
import { FENCE_PRICE_PER_M_CENTS } from '../../sim/systems/landtools';
import { IconChevron, IconFence } from '../components/icons';

// Sensible build defaults; the catalog prices them.
const DEFAULTS = { areaHa: 2, fencePerimeterM: 400, waterPoints: 1 };
/** 4A grade labels; per-metre rates come from the sim (landtools), never hardcoded here. */
const GRADES: { grade: number; name: string }[] = [
  { grade: 1, name: 'Basic' },
  { grade: 2, name: 'Heavy' },
  { grade: 3, name: 'Predator-proof' },
];

export function ParkScreen() {
  const { sim, bridge } = useStore();
  const owned = sim.parcels.filter((p) => p.owned);
  const types = Object.entries(sim.enclosureTypes);
  const [parcelId, setParcelId] = useState<string | null>(null);
  const [pickType, setPickType] = useState<string | null>(null);
  const [checkEnc, setCheckEnc] = useState('');
  const encId = checkEnc || sim.enclosures.at(0)?.id || '';
  const parcel = owned.find((p) => p.id === parcelId) ?? owned.at(0);

  // 4A: total = type base + 400m at the picked grade rate + type water rate.
  const costOf = (t: { base_cost_cents: number; water_point_cost_cents: number }, grade: number) =>
    t.base_cost_cents + (FENCE_PRICE_PER_M_CENTS[grade] ?? 0) * DEFAULTS.fencePerimeterM + t.water_point_cost_cents * DEFAULTS.waterPoints;

  return (
    <div class="screen-scroll">
      <div class="eyebrow eyebrow-rule">Park - build</div>

      {sim.enclosures.length > 0 && (
        <>
          <div class="eyebrow" style="margin-top:10px">Enclosures - {sim.enclosures.length}</div>
          {sim.enclosures.map((e) => (
            <div key={e.id} class="card row" style="border:var(--ui-border);background:var(--ui-parchment-soft)">
              <span class="row-ic"><IconFence /></span>
              <span class="row-main">
                <div class="row-value" style="text-transform:capitalize">{e.type.replace(/_/g, ' ')}</div>
                <div class="row-label">{e.areaHa} ha - {e.animalCount} animal{e.animalCount === 1 ? '' : 's'} - parcel {e.parcelId}</div>
                {/* P608 disease response: vaccination campaign + outbreak report/conceal.
                    Rejections (short cash, no active outbreak, already reported/concealed)
                    arrive as alerts; no fake acks. */}
                {(e.vaccinated === true || (e.illCount ?? 0) > 0 || e.outbreakReported === true || e.outbreakConcealed === true) && (
                  <div style="display:flex;gap:6px;flex-wrap:wrap;margin-top:6px">
                    {e.vaccinated === true && <span class="pill">Vaccinated</span>}
                    {(e.illCount ?? 0) > 0 && <span class="pill pill-red">Outbreak - {e.illCount} ill</span>}
                    {e.outbreakReported === true && <span class="pill">Reported</span>}
                    {e.outbreakConcealed === true && <span class="pill pill-red">Concealed</span>}
                  </div>
                )}
                {/* pointer-events:auto: the .row-main rule (ui.css) kills child hit-testing
                    for tappable rows; these action buttons must stay live (same pattern
                    as ReserveScreen's pointer-events:auto restore). Vaccinate disables
                    while a campaign is active: the sim charges full cost per run and a
                    re-run only extends the window, so a live button invites duplicate
                    spend. No pricing/effect change - renewal re-opens when it lapses. */}
                <div style="display:flex;gap:6px;flex-wrap:wrap;margin-top:6px;pointer-events:auto">
                  <button class="btn btn-quiet" style="padding:4px 10px;font-size:11px" disabled={e.vaccinated === true} onClick={() => { bridge.send('enclosure.vaccinate', { enclosureId: e.id }); }}>
                    {sim.standing?.vaccineCostCents ? `Vaccinate ($${fmtMoney(sim.standing.vaccineCostCents)})` : 'Vaccinate'}
                  </button>
                  {outbreakActions(e).report && (
                      <button class="btn btn-quiet" style="padding:4px 10px;font-size:11px" onClick={() => { bridge.send('disease.report', { enclosureId: e.id }); }}>Report outbreak</button>
                  )}
                  {outbreakActions(e).conceal && (
                      <button class="btn btn-quiet" style="padding:4px 10px;font-size:11px" onClick={() => { bridge.send('disease.conceal', { enclosureId: e.id }); }}>Conceal (-government rep)</button>
                  )}
                </div>
              </span>
            </div>
          ))}
        </>
      )}

      {sim.traps.length > 0 && (
        <>
          <div class="eyebrow" style="margin-top:12px">Traps - {sim.traps.length}</div>
          {sim.enclosures.length > 1 && (
            <div style="display:flex;gap:6px;flex-wrap:wrap;margin-top:6px">
              {sim.enclosures.map((e) => <button key={e.id} class={`pill${encId === e.id ? ' pill-amber' : ''}`} style="cursor:pointer" onClick={() => { setCheckEnc(e.id); }}>{e.id}</button>)}
            </div>
          )}
          {sim.traps.map((t) => (
            <div key={t.id} class="card row" style="border:var(--ui-border);background:var(--ui-parchment-soft)">
              <span class="row-main">
                <div class="row-value" style="text-transform:capitalize">{t.type.replace(/_/g, ' ')}{t.baited ? ' (baited)' : ''}</div>
                <div class="row-label">Parcel {t.parcelId} - {t.ready === undefined ? 'status unknown' : t.ready ? 'ready to check' : `ready in ${Math.max(0, Math.ceil(((t.readyTick ?? 0) - sim.tick)))} h`}</div>
              </span>
              <button class="btn btn-quiet" style="padding:4px 10px;font-size:11px" disabled={t.ready === false || encId === ''} onClick={() => { bridge.send('trap.check', { trapId: t.id, enclosureId: encId }); }}>Check</button>
            </div>
          ))}
          <div class="card" style="border:var(--ui-border);background:var(--ui-parchment-soft);margin-top:6px;padding:8px 12px">
            <div class="row-label" style="white-space:normal">A catch moves into the selected enclosure (the first one if only one exists).</div>
          </div>
        </>
      )}
      <div class="eyebrow" style="margin-top:12px">Build enclosure</div>
      {owned.length === 0 && (
        <p style="font-size:13px;color:var(--ui-ink-soft)">No owned parcels yet. Buy land from Office - Land first.</p>
      )}
      {owned.length > 1 && (
        <div style="display:flex;gap:6px;flex-wrap:wrap;margin-top:6px">
          {owned.map((p) => (
            <button
              key={p.id}
              class={`pill${parcel?.id === p.id ? ' pill-amber' : ''}`}
              style="cursor:pointer"
              onClick={() => { setParcelId(p.id); }}
            >{p.id} - {p.landClass}</button>
          ))}
        </div>
      )}
      {types.length === 0 && (
        <p style="font-size:13px;color:var(--ui-ink-soft);margin-top:8px">Catalog loads once the scenario starts.</p>
      )}
      {owned.length > 0 && types.map(([id, t]) => (
        <div key={id}>
          <button
            class="card row"
            style="border:var(--ui-border);width:100%;text-align:left;cursor:pointer;background:var(--ui-parchment-soft)"
            onClick={() => { setPickType(pickType === id ? null : id); }}
          >
            <span class="row-ic"><IconFence /></span>
            <span class="row-main">
              <div class="row-value" style="text-transform:capitalize">{id.replace(/_/g, ' ')}</div>
              <div class="row-label">up to {t.density_per_ha * DEFAULTS.areaHa} animals - {t.groups.join(', ')}</div>
            </span>
            <span class="row-chev"><IconChevron /></span>
          </button>
          {pickType === id && (
            // Mount-scroll: types low in the list expand below the fold; bring the
            // grade card into view so the tap has a visible result. Look unchanged.
            <div ref={(el) => el?.scrollIntoView({ block: 'center' })} class="card" style="border:var(--ui-border);background:var(--ui-parchment);margin-top:2px;padding:8px 12px">
              <div class="eyebrow">Fence grade - pick one</div>
              {GRADES.map(({ grade, name }) => (
                <button
                  key={grade}
                  class="btn btn-quiet"
                  style="display:flex;justify-content:space-between;width:100%;margin-top:4px;cursor:pointer"
                  onClick={() => {
                    if (!parcel) return;
                    bridge.send('enclosure.build', { parcelId: parcel.id, type: id, ...DEFAULTS, fenceGrade: grade });
                    setPickType(null);
                  }}
                >
                  <span>{name} fence - ${fmtMoney(FENCE_PRICE_PER_M_CENTS[grade] ?? 0)}/m</span>
                  <span>${fmtMoney(costOf(t, grade))}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
